import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@digdir/designsystemet-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@navikt/aksel-icons';

import { useRegistrationSteps } from '../../../hooks/useRegistrationSteps';
import { SimpleEnergyAnalysis } from '../../../../../types/building';
import { RegistrationCommonStep } from '../../../../../utils/navigation';
import { useChangeLanguage } from '../../../../expertRegistration/common/hooks/useChangeLanguage';
import { useSamtykkeSubmit } from '../../oppsummering/hooks/useSamtykkeSubmit';
import { FormContentWrapper } from '../../omBoligen/form';
import { ButtonWrapper } from '../../solceller/form/components/styledComponents';
import { useBeregnEnergimerkeSubmit } from '../../oppsummering/hooks/useBeregnEnergimerkeSubmit';
import { useGetUserInputReadOnly } from '../../../../registration/hooks/useGetUserInput';
import { ApiRequestError, toApiRequestError } from '../../../../../services/api';
import { ErrorBox } from '../../../../../components/errorBox';

import { StandardLanguagePicker } from './components/standardLanguagePicker';
import { Samtykke } from './components/samtykke';
import { VisningsOgDelingsData } from './types';

interface Props {
  registrationId: string;
  energianalyse: SimpleEnergyAnalysis;
}

export const VisningOgDelingsFrom: FC<Props> = ({ registrationId, energianalyse }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { previousStep, nextStep } = useRegistrationSteps(RegistrationCommonStep.SHARING_CONSENT);
  const { changeLanguage, malform } = useChangeLanguage(energianalyse);
  const { userInput } = useGetUserInputReadOnly(registrationId);
  const { beregnEnergimerkeAsync, beregnLoading } = useBeregnEnergimerkeSubmit(registrationId);
  const energiplan = energianalyse.energiplan;
  const [submitError, setSubmitError] = useState<ApiRequestError | null>(null);

  const { certificateVisibility, onSamtykkeSubmit, isLoading } = useSamtykkeSubmit({
    registreringId: registrationId,
    energiplan: energiplan!
  });

  const methods = useForm<VisningsOgDelingsData>({
    defaultValues: {
      maalform: malform,
      synlighet: certificateVisibility
    }
  });

  if (!userInput) {
    return null; // TODO: Vise error page da videre beregning ikke kan gjøres
  }

  const onSubmit = async (data: VisningsOgDelingsData) => {
    if (!data.synlighet) {
      return;
    }

    setSubmitError(null);

    try {
      const hasSubmittedConsent = await onSamtykkeSubmit(data.synlighet);
      if (!hasSubmittedConsent) {
        return;
      }

      changeLanguage(data.maalform);
      await beregnEnergimerkeAsync({ registrationId, userInput });
      navigate(`../${nextStep}`);
    } catch (error) {
      setSubmitError(toApiRequestError(error, 'Could not submit visning og deling'));
    }
  };

  const isFormDisabled = isLoading || beregnLoading;

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
        <FormContentWrapper>
          <StandardLanguagePicker disabled={isFormDisabled} />
          <Samtykke disabled={isFormDisabled} />
          <ErrorBox error={submitError} registrationId={registrationId} />
          <ButtonWrapper>
            <Button
              variant="secondary"
              type="button"
              disabled={isFormDisabled}
              onClick={() => navigate(`../${previousStep}`)}
            >
              <ChevronLeftIcon /> {t('form.common.previous')}
            </Button>
            <Button type="submit" loading={isLoading || beregnLoading}>
              {t('form.common.next')} <ChevronRightIcon />
            </Button>
          </ButtonWrapper>
        </FormContentWrapper>
      </form>
    </FormProvider>
  );
};
